import { FC, Fragment, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { reportsDataNotificationsSelector } from "@/store/reports/selector/reportsDataNotificationsSelector";
import { IntegrationSelect } from "@/components/common/integration-dialogs/components/common/IntegrationSelect";
import { SlackConversationsSelect } from "@/components/common/integration-dialogs/components/slack/SlackConversationsSelect";
import { setReportsData } from "@/store/reports/reportsSlice";
import {
  NotificationSlack,
  SlackConversation,
} from "@/services/cloudchipr.api";
import { CreateIntegrationMessageEditor } from "@/components/common/integration-dialogs/components/common/CreateIntegrationMessageEditor";
import { CommunicationIntegrationDrawer } from "@/components/pages/integrations/components/communications/common/components/drawer/CommunicationIntegrationDrawer";

export const SendReportSlackSection: FC = () => {
  const dispatch = useAppDispatch();
  const notification = useAppSelector(reportsDataNotificationsSelector);

  const integrationIdChangeHandler = useCallback(
    (id: string) => {
      dispatch(
        setReportsData({
          notifications: [
            {
              ...notification,
              integration_id: id,
              type: "slack",
            } as NotificationSlack,
          ],
        }),
      );
    },
    [notification, dispatch],
  );

  const conversationsChangeHandler = useCallback(
    (conversations: SlackConversation[]) => {
      dispatch(
        setReportsData({
          notifications: [
            {
              ...notification,
              conversations,
              type: "slack",
            } as NotificationSlack,
          ],
        }),
      );
    },
    [notification, dispatch],
  );

  const messageChangeHandler = useCallback(
    (value: string) => {
      dispatch(
        setReportsData({
          notifications: [
            {
              ...notification,
              custom_message: value,
              type: "slack",
            } as NotificationSlack,
          ],
        }),
      );
    },
    [dispatch, notification],
  );

  const mentionsChangeHandler = useCallback(
    (mentions: SlackConversation[]) => {
      dispatch(
        setReportsData({
          notifications: [
            {
              ...notification,
              mentions: mentions,
              type: "slack",
            } as NotificationSlack,
          ],
        }),
      );
    },
    [dispatch, notification],
  );

  return (
    <Fragment>
      <IntegrationSelect
        type="slack"
        value={notification?.integration_id ?? ""}
        onChange={integrationIdChangeHandler}
      />

      <SlackConversationsSelect
        onChange={conversationsChangeHandler}
        disabled={!notification?.integration_id}
        integrationId={notification?.integration_id ?? ""}
        selectedConversations={notification?.conversations}
      />

      <CreateIntegrationMessageEditor
        value={notification?.custom_message ?? ""}
        onChange={messageChangeHandler}
        mention={{
          integrationId: notification?.integration_id ?? "",
          selected: notification?.mentions ?? [],
          onChange: mentionsChangeHandler,
        }}
      />

      <CommunicationIntegrationDrawer />
    </Fragment>
  );
};
